import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { getChannel, setChannelSponsor } from '../lib/api';
import { toast } from 'sonner';
import AdManager from '../components/AdManager';
import { Megaphone, Save, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Ads() {
  const { user } = useApp();
  const [channel, setChannel] = useState(null);
  const [form, setForm] = useState({ sponsor_name: '', sponsor_tagline: '', sponsor_logo: '', sponsor_url: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!user?.channel_id) { setLoading(false); return; }
      try {
        const c = await getChannel(user.channel_id);
        setChannel(c);
        setForm({
          sponsor_name: c.sponsor_name || '',
          sponsor_tagline: c.sponsor_tagline || '',
          sponsor_logo: c.sponsor_logo || '',
          sponsor_url: c.sponsor_url || '',
        });
      } finally { setLoading(false); }
    })();
  }, [user]);

  const saveSponsor = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const c = await setChannelSponsor(channel.id, form); setChannel(c); toast.success('Sponsor saved'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  if (!user?.channel_id) {
    return (
      <div className="py-10"><div className="card-shell p-6 max-w-[560px]"><h2 className="font-display text-[24px]">No channel</h2><p className="text-white/65 mt-1 text-[14px]">You need a creator channel to run ads. Sign up as a creator.</p></div></div>
    );
  }

  return (
    <div>
      <div className="chip"><Megaphone className="w-3.5 h-3.5 mr-1" /> Ads & Sponsorships</div>
      <h1 className="font-display text-[32px] md:text-[40px] mt-3 leading-none">Monetize your audience</h1>
      <p className="text-white/60 text-[14px] mt-1">Promote your own videos in category rows, set a sponsor block on your <Link to={`/channel/${channel.id}`} className="text-[#ff6b8a] hover:underline">channel</Link>, or partner with brands.</p>

      <Link to="/ads/marketplace" className="card-shell p-5 mt-6 flex items-center justify-between hover-lift block" style={{ borderColor: 'rgba(255,207,82,0.35)', background: 'linear-gradient(90deg, rgba(255,207,82,0.08), rgba(255,46,99,0.06))' }} data-testid="ads-marketplace-cta">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: 'rgba(255,207,82,0.15)', border: '1px solid rgba(255,207,82,0.4)' }}>
            <Megaphone className="w-5 h-5 text-[#ffcf52]" />
          </div>
          <div>
            <div className="font-semibold text-[15px]">Ad Marketplace — preview & buy</div>
            <div className="text-[13px] text-white/60">See your ad live in every placement, then buy a flat-rate block with Stripe.</div>
          </div>
        </div>
        <div className="text-[#ffcf52] text-[13px]">Open →</div>
      </Link>

      <div className="card-shell p-6 mt-8">
        <h3 className="row-title text-white">Channel sponsor block</h3>
        <p className="text-white/60 text-[13px] mt-1">Shown at the top of your channel page. For a rotating sponsor ad, create a “Channel sponsor” ad below instead.</p>
        <form onSubmit={saveSponsor} className="mt-5 grid md:grid-cols-2 gap-4">
          <Field label="Sponsor name" value={form.sponsor_name} onChange={v => setForm({ ...form, sponsor_name: v })} />
          <Field label="Tagline" value={form.sponsor_tagline} onChange={v => setForm({ ...form, sponsor_tagline: v })} />
          <Field label="Logo URL" value={form.sponsor_logo} onChange={v => setForm({ ...form, sponsor_logo: v })} />
          <Field label="Destination URL" value={form.sponsor_url} onChange={v => setForm({ ...form, sponsor_url: v })} />
          <div className="md:col-span-2">
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60"><Save className="w-4 h-4" /> Save sponsor</button>
          </div>
        </form>
      </div>

      <div className="mt-8">
        <AdManager ownerMode={false} />
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-[12.5px] text-white/70">{label}</span>
      <input value={value} onChange={e => onChange(e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ff2e63]/60 text-[14px]" />
    </label>
  );
}
